import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import { Button, TextField } from "@mui/material";

import MainContainer from "../../layout/MainContainer";

import { errorToast, successToast } from "../../helpers";
import { isNumberPrice } from "../../utils/validations";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import Loader from "../../constants/Loader";
import {
  useAddSalaryStructureMutation,
  useLazySalaryStructureDetailQuery,
  useUpdateSalaryStructureMutation,
} from "../../service/salaryStructure";
import { USER_ROLES } from "../../utils/enums";
import { formatNumberWithCommas } from "../../utils/commoFunctions";

const AddSalaries = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [getSalarySlab] = useLazySalaryStructureDetailQuery();
  const [addSalarySlab] = useAddSalaryStructureMutation();
  const [updateSalarySlab] = useUpdateSalaryStructureMutation();

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await getSalarySlab({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        formik.setFieldValue(
          "min_revenue",
          formatNumberWithCommas(response?.data?.min_revenue?.toString() || "0")
        );
        formik.setFieldValue(
          "max_revenue",
          formatNumberWithCommas(response?.data?.max_revenue?.toString() || "0")
        );
        formik.setFieldValue(
          "basic_salary",
          formatNumberWithCommas(
            response?.data?.basic_salary?.toString() || "0"
          )
        );
        formik.setFieldValue(
          "bonus",
          formatNumberWithCommas(response?.data?.bonus?.toString() || "0")
        );
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      min_revenue: "",
      max_revenue: "",
      basic_salary: "",
      bonus: "",
    },

    validationSchema: Yup.object({
      min_revenue: Yup.string().required("This field is required"),
      max_revenue: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 digits are required")
        .test(
          "is-greater",
          "Max revenue should be greater than min revenue",
          (value: string, context: { parent: { min_revenue: string } }) => {
            if (!value || !context.parent.min_revenue) return true; // Skip check if either field is empty
            const minRevenue = Number(
              context.parent.min_revenue.replace(/,/g, "")
            );
            const maxRevenue = Number(value.replace(/,/g, ""));
            return maxRevenue > minRevenue;
          }
        ),
      basic_salary: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 digits are required"),
      bonus: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 digits are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        min_revenue: Number(values.min_revenue?.replace(/,/g, "")),
        max_revenue: Number(values.max_revenue?.replace(/,/g, "")),
        basic_salary: Number(values.basic_salary?.replace(/,/g, "")),
        bonus: Number(values.bonus?.replace(/,/g, "")),
        user_type: USER_ROLES.SALARIED_AGENT,
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateSalarySlab({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addSalarySlab(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Salary slab updated successfully");
          } else {
            successToast("Salary slab added successfully");
          }
          navigate("/manage-salaries");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Salary Slab </h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-salaries")}>
              Salary Slabs
            </span>{" "}
            - {id ? "Edit" : "Add"} Salary Slab
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Basic Revenue (min)</label>
              <TextField
                variant="outlined"
                placeholder="Basic Revenue (min)"
                fullWidth
                hiddenLabel
                className="text_field"
                name="min_revenue"
                id="min_revenue"
                inputProps={{ maxLength: 11 }}
                value={formik.values.min_revenue}
                onChange={(val) => {
                  const { value } = val.target;

                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("min_revenue", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.min_revenue && formik.errors.min_revenue
                }
              />
            </div>
            <div className="control_group w_50">
              <label>Basic Revenue (max)</label>
              <TextField
                variant="outlined"
                placeholder="Basic Revenue (max)"
                fullWidth
                hiddenLabel
                className="text_field"
                name="max_revenue"
                id="max_revenue"
                inputProps={{ maxLength: 11 }}
                value={formik.values.max_revenue}
                onChange={(val) => {
                  const { value } = val.target;

                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("max_revenue", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.max_revenue && formik.errors.max_revenue
                }
              />
            </div>
            <div className="control_group w_50">
              <label>Basic Salary</label>
              <TextField
                variant="outlined"
                placeholder="Basic Salary"
                fullWidth
                hiddenLabel
                className="text_field"
                name="basic_salary"
                id="basic_salary"
                inputProps={{ maxLength: 11 }}
                value={formik.values.basic_salary}
                onChange={(val) => {
                  const { value } = val.target;

                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("basic_salary", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.basic_salary && formik.errors.basic_salary
                }
              />
            </div>
            <div className="control_group w_50">
              <label>Bonus</label>
              <TextField
                variant="outlined"
                placeholder="Bonus"
                fullWidth
                hiddenLabel
                className="text_field"
                name="bonus"
                id="bonus"
                inputProps={{ maxLength: 11 }}
                value={formik.values.bonus}
                onChange={(val) => {
                  const { value } = val.target;

                  // Remove commas for proper validation and length counting
                  let numericValue = value.replace(/,/g, "");

                  // Prevent starting with 0 unless it's just "0"
                  if (numericValue.length && numericValue.startsWith("0")) {
                    numericValue = numericValue.replace(/^0+/, ""); // Remove leading zeros
                  }

                  // Validate number format and length limit
                  if (isNumberPrice(numericValue) && numericValue.length <= 9) {
                    const formattedValue = formatNumberWithCommas(numericValue);
                    formik.setFieldValue("bonus", formattedValue);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.bonus && formik.errors.bonus}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddSalaries;
